import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Briefcase, User as UserIcon, Users as UsersIcon } from 'lucide-react';
import type {
  EntityOrSuggestionRef,
  EntitySuggestion,
  OrgFunction,
  OrgFunctionKind,
  User
} from '@bliks-io/contracts';
import { EntityPickerBody } from '@bliks-io/ui';
import { useOrgFunctionsForLookup } from '@/api/queries/useOrgFunctions';
import type { CreatedSuggestionSession } from '../hooks/useCreatedSuggestionSession';
import { usePersonPickerConfig } from '../hooks/usePersonPickerConfig';
import { useOrgFunctionPickerConfig } from '../hooks/useOrgFunctionPickerConfig';
import {
  buildPendingOrgFunctionItems,
  isPendingId,
  toEntityOrSuggestionRef,
  toPendingId
} from '../shared/pendingSuggestionUtils';
import { recommendationRecordingId } from '../shared/recommendationScope';
import { suggestionPickId } from '../shared/suggestionPickEvent';
import { TypePickerModalShell } from '../type-picker/TypePickerModalShell';
import { TypePickerNav, type TypePickerNavSectionInput } from '../type-picker/TypePickerNav';
import { useBodyRefsByType, useTentativeByType } from '../type-picker/useTypePickerState';

/**
 * The nav types of the helpers picker. `team` and `role` are two presentation
 * panes over the ONE stored `helpers.orgFunctions[]` union-ref slot (ADR
 * 0004) — pane membership carries the kind, the confirm re-unifies them.
 */
export type HelperTypeKey = 'person' | 'team' | 'role';

const HELPER_TYPE_KEYS = ['person', 'team', 'role'] as const;

/**
 * The person/function split of ONE confirm. Emitted as a single payload:
 * editor consumers rebuild the whole `helpers` object from siblings captured
 * at render time, so two sequential per-slot callbacks would let the second
 * restore the first array's stale value (plan 026, D4).
 */
export interface HelpersSelection {
  persons: string[];
  orgFunctions: EntityOrSuggestionRef[];
}

export interface HelpersEntityPickerModalProps {
  open: boolean;
  /** Fires on cancel/escape/close only — a confirm reports via `onConfirm`. */
  onOpenChange: (open: boolean) => void;
  /** Current slot values; the modal seeds its per-type draft from them. */
  persons: string[];
  orgFunctions: EntityOrSuggestionRef[];
  onConfirm: (next: HelpersSelection) => void;
  processId?: string;
  recordingId?: string;
  /** Draft-editor host: create/link post to the draft suggestion routes. */
  draftProcessId?: string;
  /** Pending suggestions on the source node, surfaced as selectable rows. */
  pendingSuggestions?: EntitySuggestion[];
  /**
   * Ledger of suggestions created in helper sessions. Owned by the caller —
   * the outermost scope that can still cancel them — which settles it at the
   * session boundaries (cell open, cancelled close). The modal only tracks
   * creates and names their rows from it.
   */
  session: CreatedSuggestionSession;
  /** Editor opt-in: create Teams / Funktionen directly instead of suggesting. */
  allowCreate?: boolean;
}

const HELPERS_MODAL_SIZES = {
  expanded: { width: 1120, minHeight: 800 }
} as const;

/**
 * Seed the per-type draft from the mixed slot values. Kind resolution uses
 * the deleted-INCLUSIVE lookup (a stored soft-deleted helper has no live
 * row) and the suggestion payloads; an unresolvable kind files under `role`.
 * The draft reseeds while pristine when either source arrives late, and the
 * confirm dedupes the two function panes, so a misfiled kind can neither
 * duplicate nor corrupt the slot.
 */
function seedTentativeByType(args: {
  persons: string[];
  orgFunctions: EntityOrSuggestionRef[];
  lookupKindById: ReadonlyMap<string, OrgFunctionKind>;
  suggestionKindById: ReadonlyMap<string, OrgFunctionKind>;
}): Partial<Record<HelperTypeKey, string[]>> {
  const team: string[] = [];
  const role: string[] = [];
  for (const ref of args.orgFunctions) {
    const kind =
      ref.type === 'suggestion'
        ? args.suggestionKindById.get(ref.id)
        : args.lookupKindById.get(ref.id);
    const id = ref.type === 'suggestion' ? toPendingId(ref.id) : ref.id;
    (kind === 'team' ? team : role).push(id);
  }
  return { person: [...args.persons], team, role };
}

/**
 * Master-detail picker for the participants cell: entity types on the left
 * (persons / teams / roles), that type's suggestions and list on the right.
 * Opens directly in the master-detail state — no chooser page. Selection is
 * multi across all types and confirms as one atomic {@link HelpersSelection}.
 */
export function HelpersEntityPickerModal({
  open,
  onOpenChange,
  persons,
  orgFunctions,
  onConfirm,
  processId,
  recordingId,
  draftProcessId,
  pendingSuggestions,
  session,
  allowCreate
}: HelpersEntityPickerModalProps) {
  const { t } = useTranslation();
  const [currentType, setCurrentType] = useState<HelperTypeKey>('person');

  // Mirrors the active picker's bucket↔all viewMode so the footer can render
  // a "back to suggestions" affordance.
  const [pickerViewMode, setPickerViewMode] = useState<'bucket' | 'all'>('bucket');

  const { selectedIdsFor, updateTentative, resetTentative } = useTentativeByType<HelperTypeKey>();
  const bodyRefs = useBodyRefsByType(HELPER_TYPE_KEYS);

  // Set on every user edit; while false the draft is pristine and may be
  // reseeded when late classification data lands.
  const draftDirtyRef = useRef(false);
  const updateDraft = useCallback(
    (type: HelperTypeKey, ids: string[]) => {
      draftDirtyRef.current = true;
      updateTentative(type, ids);
    },
    [updateTentative]
  );

  // Suggestions created here: the create response is the only source for their
  // rows — the caller's `pendingSuggestions` list doesn't carry them until the
  // ref is persisted, and on the editor's step panel that list never refetches
  // after a create at all.
  const mergedPendingSuggestions = useMemo(
    () => session.mergeInto(pendingSuggestions),
    [session, pendingSuggestions]
  );

  // The deleted-inclusive list: seeding must kind-classify stored helpers that
  // point at soft-deleted functions, which the live picker rows exclude. This
  // tree already subscribes to it for the previews, so it costs no extra fetch.
  const { data: lookupOrgFunctions } = useOrgFunctionsForLookup();
  const lookupKindById = useMemo(
    () => new Map((lookupOrgFunctions ?? []).map((fn) => [fn._id, fn.kind] as const)),
    [lookupOrgFunctions]
  );
  const suggestionKindById = useMemo(
    () =>
      new Map(
        (mergedPendingSuggestions ?? [])
          .filter(
            (s): s is Extract<EntitySuggestion, { kind: 'orgFunction' }> => s.kind === 'orgFunction'
          )
          .map((s) => [s._id, s.payload.kind] as const)
      ),
    [mergedPendingSuggestions]
  );

  const recommendationScopeId = recommendationRecordingId(recordingId, draftProcessId);

  const personHook = usePersonPickerConfig({
    open: open && currentType === 'person',
    mode: 'multi',
    processId,
    recordingId: recommendationScopeId
  });
  // Team and role are two presentation panes over the ONE orgFunctions slot
  // (ADR 0004), and only one is ever active. A single kind-following config
  // serves whichever pane is open, so the inactive kind's list is never
  // fetched. The exiting pane keeps its own data because DialogPhasePanel
  // freezes the outgoing layer's rendered element during the crossfade.
  const activeFunctionKind = currentType === 'role' ? 'role' : 'team';
  const orgFunctionHook = useOrgFunctionPickerConfig({
    open: open && currentType !== 'person',
    mode: 'multi',
    kind: activeFunctionKind,
    processId,
    recordingId: recommendationScopeId,
    draftProcessId,
    requestContext: recordingId
      ? {
          sourceStepRecordingId: recordingId,
          sourceStepRecordingPlace: 'helpers_org_function'
        }
      : undefined,
    selectedIds: selectedIdsFor(activeFunctionKind),
    pendingSuggestions: mergedPendingSuggestions,
    allowCreate
  });

  const buildSeed = () =>
    seedTentativeByType({ persons, orgFunctions, lookupKindById, suggestionKindById });

  // Reset-on-open (render-phase, mirroring EntityPickerModal): this wrapper
  // stays mounted across close, so a canceled draft or a stale pane must not
  // leak into the next session. While the draft is pristine, a late-arriving
  // classification source (cold lookup cache, refetched suggestions) reseeds
  // it, so a stored team ref doesn't stay misfiled in the role pane.
  const [prevOpen, setPrevOpen] = useState(open);
  const [prevSeedSources, setPrevSeedSources] = useState({
    lookupKindById,
    suggestionKindById
  });
  const seedSourcesChanged =
    prevSeedSources.lookupKindById !== lookupKindById ||
    prevSeedSources.suggestionKindById !== suggestionKindById;
  if (prevOpen !== open) {
    setPrevOpen(open);
    if (open) {
      setCurrentType('person');
      setPickerViewMode('bucket');
      setPrevSeedSources({ lookupKindById, suggestionKindById });
      draftDirtyRef.current = false;
      resetTentative(buildSeed());
    }
  } else if (open && seedSourcesChanged) {
    setPrevSeedSources({ lookupKindById, suggestionKindById });
    if (!draftDirtyRef.current) {
      resetTentative(buildSeed());
    }
  }

  const handleSelectType = useCallback(
    (next: HelperTypeKey) => {
      setCurrentType(next);
      setPickerViewMode('bucket');
      // A fresh pane always lands in bucket view, where the department rail
      // (an 'all'-view affordance) isn't shown — so a carried-over filter would
      // be invisible and unresettable. Clear it on every pane change.
      orgFunctionHook.config.departmentFilter?.onChange(null);
      personHook.config.departmentFilter?.onChange(null);
    },
    [orgFunctionHook, personHook]
  );

  const sections = useMemo<TypePickerNavSectionInput<HelperTypeKey>[]>(
    () => [
      {
        id: 'type',
        options: [
          { value: 'person', icon: UserIcon, label: t('entityPicker.helper.kind.person') },
          { value: 'team', icon: UsersIcon, label: t('entityPicker.helper.kind.team') },
          { value: 'role', icon: Briefcase, label: t('entityPicker.helper.kind.role') }
        ]
      }
    ],
    [t]
  );

  // Last resort so every selected id has a row to uncheck. Only a prefixed id
  // can be missing a row: entity ids the panes don't list are soft-deleted
  // entities, which stay out of the list by design (plan 026, D4a). An
  // unresolvable pending ref always seeds into the role pane, so the
  // placeholder only ever joins that pane's items. `known` derives from the
  // same builder as the pane's ghost rows, so a selected suggestion the pane
  // does NOT render (wrong kind, no longer pending) still gets a placeholder.
  const unnamedPendingRoleItems = useMemo<OrgFunction[]>(() => {
    const known = new Set(
      buildPendingOrgFunctionItems(mergedPendingSuggestions, 'role').map((item) => item._id)
    );
    const missing = selectedIdsFor('role').filter((id) => isPendingId(id) && !known.has(id));
    const label = t('viralRecording.helpersHeading.pendingFunction');
    return missing.map((id) => ({ _id: id, name: label, kind: 'role', email: '' }) as OrgFunction);
  }, [mergedPendingSuggestions, selectedIdsFor, t]);

  const functionPaneItems = useMemo<OrgFunction[]>(
    () =>
      activeFunctionKind === 'role' && unnamedPendingRoleItems.length > 0
        ? [...orgFunctionHook.items, ...unnamedPendingRoleItems]
        : orgFunctionHook.items,
    [activeFunctionKind, orgFunctionHook.items, unnamedPendingRoleItems]
  );

  const totalSelected =
    selectedIdsFor('person').length + selectedIdsFor('team').length + selectedIdsFor('role').length;
  const ctaLabel =
    totalSelected === 1
      ? t('entityPicker.helper.saveSingular', { count: 1 })
      : t('entityPicker.helper.savePlural', { count: totalSelected });

  const handleConfirm = () => {
    // Dedupe across the two function panes: a reseed race or a cross-kind
    // adoption could file one id in both, and the slot must never carry the
    // same ref twice.
    const functionIds = [...new Set([...selectedIdsFor('team'), ...selectedIdsFor('role')])];
    onConfirm({
      persons: selectedIdsFor('person'),
      orgFunctions: functionIds.map(toEntityOrSuggestionRef)
    });
  };

  /**
   * File a function id picked from the suggest dialog into the pane matching
   * its ACTUAL kind — the duplicate panel is not kind-narrowed, so the team
   * pane can hand out a role (and vice versa). An id of unknown kind stays in
   * the active pane, where the user just saw it.
   */
  const addFunctionToKindPane = (id: string, kind: OrgFunctionKind | undefined) => {
    const pane: HelperTypeKey = kind ?? activeFunctionKind;
    const current = selectedIdsFor(pane);
    if (!current.includes(id)) updateDraft(pane, [...current, id]);
  };

  const activeBodyRef = bodyRefs[currentType];

  const handleEscapeKeyDown = (event: globalThis.KeyboardEvent) => {
    if (
      activeBodyRef.current?.getViewMode() === 'all' &&
      activeBodyRef.current.canReturnToBuckets()
    ) {
      event.preventDefault();
      activeBodyRef.current.backToBuckets();
    }
  };

  return (
    <TypePickerModalShell
      open={open}
      onOpenChange={onOpenChange}
      sizes={HELPERS_MODAL_SIZES}
      size="expanded"
      headerIcon={<UsersIcon className="h-5 w-5" />}
      title={t('entityPicker.helper.title')}
      subtitle={t('entityPicker.helper.description')}
      sidebar={
        <TypePickerNav sections={sections} currentType={currentType} onSelect={handleSelectType} />
      }
      resizableAutoSaveId="helpers-picker-sidebar"
      phaseKey={currentType}
      onBackToBuckets={
        pickerViewMode === 'all' && activeBodyRef.current?.canReturnToBuckets()
          ? () => activeBodyRef.current?.backToBuckets()
          : undefined
      }
      primary={{ label: ctaLabel, onClick: handleConfirm }}
      onEscapeKeyDown={handleEscapeKeyDown}
    >
      {currentType === 'person' ? (
        <EntityPickerBody<User>
          key="person"
          ref={bodyRefs.person}
          items={personHook.items}
          isLoading={personHook.isLoading}
          selectedIds={selectedIdsFor('person')}
          mode="multi"
          config={personHook.config}
          autofocusSearch={false}
          onDraftChange={(ids) => updateDraft('person', ids)}
          onViewModeChange={setPickerViewMode}
        />
      ) : (
        <>
          {/* `key`/`ref` stay per-kind so a team↔role crossfade keeps the
              exiting body's ref from racing the entering one's cleanup. */}
          <EntityPickerBody<OrgFunction>
            key={currentType}
            ref={bodyRefs[currentType]}
            items={functionPaneItems}
            isLoading={orgFunctionHook.isLoading}
            selectedIds={selectedIdsFor(currentType)}
            mode="multi"
            config={orgFunctionHook.config}
            suggestNew={orgFunctionHook.suggestNew}
            autofocusSearch={false}
            onDraftChange={(ids) => updateDraft(currentType, ids)}
            onViewModeChange={setPickerViewMode}
          />
          {orgFunctionHook.renderSuggestDialog({
            // An existing entity taken from the duplicate panel joins the
            // draft (in the pane of its actual kind) instead of confirming
            // for the user. `next` arrives unioned with the active pane's
            // selection — only the additions are filed.
            onUseExistingEntity: (next) => {
              const current = new Set(selectedIdsFor(activeFunctionKind));
              for (const id of next) {
                if (!current.has(id)) addFunctionToKindPane(id, lookupKindById.get(id));
              }
            },
            onSuggestionCreated: (event) => {
              // A created/adopted suggestion joins the draft as a checked row:
              // the user confirms the whole helper set in one go, and the
              // prefixed id round-trips back into a suggestion ref at confirm.
              if (event.kind === 'created') session.track(event.suggestion);
              const suggestionId = suggestionPickId(event);
              const kind =
                event.kind === 'created' && event.suggestion.kind === 'orgFunction'
                  ? event.suggestion.payload.kind
                  : suggestionKindById.get(suggestionId);
              addFunctionToKindPane(toPendingId(suggestionId), kind);
            }
          })}
        </>
      )}
    </TypePickerModalShell>
  );
}
